import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lightning, Eye, EyeSlash, Envelope, Drop, ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success(t("auth.login_success"));
      if (result.user.role === "admin") {
        navigate("/admin");
      } else if (result.user.role === "landlord") {
        navigate("/dashboard");
      } else {
        navigate("/tenant");
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-md mx-auto animate-fade-in">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          {/* Logo */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 bg-[#4F5BD5] rounded-xl flex items-center justify-center shadow-lg shadow-[#4F5BD5]/20">
              <Lightning size={24} weight="fill" className="text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-800 tracking-tight">
              NANOMET
            </h1>
          </div>
          <p className="text-slate-400 text-sm mb-10 ml-[3.25rem]">
            {t("auth.tagline")}
          </p>

          {/* Form */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-800">{t("auth.login_title")}</h2>
              <p className="text-slate-400 text-sm mt-1">{t("auth.login_subtitle")}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                  <Envelope size={16} className="text-slate-400" />
                  {t("common.email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.email_placeholder")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                  className="h-12 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#4F5BD5] focus:ring-[#4F5BD5]/10 transition-all text-base"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-600">{t("common.password")}</label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                    className="h-12 rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-[#4F5BD5] focus:ring-[#4F5BD5]/10 transition-all pr-11 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#4F5BD5] to-[#6C63FF] hover:from-[#4349C0] hover:to-[#5B53EE] text-white font-semibold text-base shadow-lg shadow-[#4F5BD5]/25 transition-all duration-200 hover:shadow-xl hover:shadow-[#4F5BD5]/30 gap-2"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? t("auth.logging_in") : t("auth.login_title")}
                {!loading && <ArrowRight size={20} weight="bold" />}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Animated Visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #0a1628 0%, #0d1f3c 50%, #0a1628 100%)' }}>
        {/* Animated grid background */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(79,91,213,0.08) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />

        {/* Animated flowing lines - electricity */}
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="elecGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="40%" stopColor="#f59e0b" stopOpacity="0.6" />
              <stop offset="60%" stopColor="#fbbf24" stopOpacity="0.8" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
            <linearGradient id="waterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="40%" stopColor="#22d3ee" stopOpacity="0.6" />
              <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.8" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {/* Horizontal electricity lines */}
          <line x1="-200" y1="25%" x2="120%" y2="25%" stroke="url(#elecGrad)" strokeWidth="2" filter="url(#glow)">
            <animateTransform attributeName="transform" type="translate" values="-300,0;800,0" dur="4s" repeatCount="indefinite" />
          </line>
          <line x1="-200" y1="75%" x2="120%" y2="75%" stroke="url(#elecGrad)" strokeWidth="1.5" filter="url(#glow)">
            <animateTransform attributeName="transform" type="translate" values="800,0;-300,0" dur="5s" repeatCount="indefinite" />
          </line>
          {/* Vertical water lines */}
          <line x1="30%" y1="-200" x2="30%" y2="120%" stroke="url(#waterGrad)" strokeWidth="2" filter="url(#glow)">
            <animateTransform attributeName="transform" type="translate" values="0,-300;0,800" dur="5s" repeatCount="indefinite" />
          </line>
          <line x1="70%" y1="-200" x2="70%" y2="120%" stroke="url(#waterGrad)" strokeWidth="1.5" filter="url(#glow)">
            <animateTransform attributeName="transform" type="translate" values="0,800;0,-300" dur="4.5s" repeatCount="indefinite" />
          </line>
        </svg>

        {/* Radial glows */}
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-amber-500/8 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-cyan-500/8 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '4s' }} />

        {/* Main content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full px-12">
          
          {/* Animated meter gauges */}
          <div className="flex gap-10 mb-10">
            {/* Electricity gauge */}
            <div className="relative group">
              <div className="w-36 h-36 rounded-full border-2 border-amber-500/20 flex items-center justify-center relative"
                style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.06) 0%, transparent 70%)' }}>
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 144 144">
                  <circle cx="72" cy="72" r="64" fill="none" stroke="rgba(245,158,11,0.1)" strokeWidth="4" />
                  <circle cx="72" cy="72" r="64" fill="none" stroke="#f59e0b" strokeWidth="4"
                    strokeDasharray="402" strokeDashoffset="402" strokeLinecap="round"
                    style={{ animation: 'elecDash 3s ease-in-out infinite alternate' }} />
                </svg>
                <div className="text-center">
                  <Lightning size={28} weight="fill" className="text-amber-400 mx-auto mb-1" />
                  <span className="text-amber-400/80 text-xs font-mono font-bold tracking-wider">kWh</span>
                </div>
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
                <span className="text-amber-400 text-[10px] font-bold tracking-widest uppercase">{t("auth.electricity")}</span>
              </div>
            </div>

            {/* Water gauge */}
            <div className="relative group">
              <div className="w-36 h-36 rounded-full border-2 border-cyan-500/20 flex items-center justify-center relative"
                style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 70%)' }}>
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 144 144">
                  <circle cx="72" cy="72" r="64" fill="none" stroke="rgba(6,182,212,0.1)" strokeWidth="4" />
                  <circle cx="72" cy="72" r="64" fill="none" stroke="#06b6d4" strokeWidth="4"
                    strokeDasharray="402" strokeDashoffset="402" strokeLinecap="round"
                    style={{ animation: 'waterDash 4s ease-in-out infinite alternate' }} />
                </svg>
                <div className="text-center">
                  <Drop size={28} weight="fill" className="text-cyan-400 mx-auto mb-1" />
                  <span className="text-cyan-400/80 text-xs font-mono font-bold tracking-wider">m³</span>
                </div>
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                <span className="text-cyan-400 text-[10px] font-bold tracking-widest uppercase">{t("auth.water")}</span>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="text-center max-w-sm">
            <h2 className="text-3xl font-heading font-bold text-white mb-1">
              {t("auth.smart_mgmt")}
            </h2>
            <p className="text-lg font-heading font-semibold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-cyan-400 mb-8">
              {t("auth.one_platform")}
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold text-white font-mono" style={{ animation: 'countUp 2s ease-out forwards' }}>7/24</div>
                <div className="text-[10px] text-slate-400 mt-0.5 tracking-wide uppercase">{t("auth.control_247")}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold text-amber-400 font-mono">kWh</div>
                <div className="text-[10px] text-slate-400 mt-0.5 tracking-wide uppercase">{t("auth.prepaid")}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 backdrop-blur-sm">
                <div className="text-2xl font-bold text-cyan-400 font-mono">m³</div>
                <div className="text-[10px] text-slate-400 mt-0.5 tracking-wide uppercase">{t("auth.tracking")}</div>
              </div>
            </div>
          </div>

          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute rounded-full"
              style={{
                width: `${3 + (i % 3) * 2}px`,
                height: `${3 + (i % 3) * 2}px`,
                background: i % 2 === 0 ? '#f59e0b' : '#06b6d4',
                left: `${15 + i * 14}%`,
                top: `${20 + (i * 17) % 60}%`,
                opacity: 0.4,
                animation: `float${i % 3} ${3 + i * 0.5}s ease-in-out infinite`,
                boxShadow: `0 0 ${6 + i * 2}px ${i % 2 === 0 ? '#f59e0b' : '#06b6d4'}`,
              }}
            />
          ))}

          {/* Company info footer */}
          <div className="absolute bottom-6 left-0 right-0 text-center px-6">
            <p className="text-slate-400/60 text-[11px] leading-relaxed">
              NANOMET, bir <span className="text-slate-300/70 font-semibold">Elworks Technologies</span> markasıdır.
            </p>
            <p className="text-slate-500/50 text-[10px] mt-1.5 leading-relaxed">
              0548 844 28 18 &nbsp;·&nbsp; Bedrettin Demirel Caddesi, Kumsal, Lefkoşa
            </p>
          </div>
        </div>

        <style>{`
          @keyframes elecDash {
            from { stroke-dashoffset: 402; }
            to { stroke-dashoffset: 120; }
          }
          @keyframes waterDash {
            from { stroke-dashoffset: 402; }
            to { stroke-dashoffset: 160; }
          }
          @keyframes float0 {
            0%, 100% { transform: translateY(0) scale(1); opacity: 0.3; }
            50% { transform: translateY(-30px) scale(1.3); opacity: 0.7; }
          }
          @keyframes float1 {
            0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; }
            50% { transform: translateY(-20px) translateX(15px); opacity: 0.6; }
          }
          @keyframes float2 {
            0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
            50% { transform: translateY(-40px) scale(0.8); opacity: 0.8; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default LoginPage;
